'use client';

import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { reduxLogger } from '@/core/utils/logger';
import { lightTheme, darkTheme, cssVariableMap } from '@/styles/theme';
import { ThemeColors } from '@/shared/types/theme';
import { getStorageRaw, setStorageItem, STORAGE_KEYS } from '@/core/utils/local-storage';

// Add TypeScript declaration for the global window property
declare global {
  interface Window {
    __INITIAL_THEME__?: string;
  }
}

type ThemeMode = 'light' | 'dark' | 'system';

// Function to get initial theme from localStorage or system preference
const getInitialThemeMode = (): ThemeMode => {
  if (typeof window === 'undefined') {
    // For SSR, always return 'light' to keep deterministic output.
    return 'light';
  }

  const storedTheme = getStorageRaw(STORAGE_KEYS.THEME_PREFERENCE) as ThemeMode | null;
  if (storedTheme === 'dark' || storedTheme === 'light' || storedTheme === 'system') {
    return storedTheme;
  }

  setStorageItem(STORAGE_KEYS.THEME_PREFERENCE, 'light');
  return 'light';
};

interface ThemeContextType {
  theme: ThemeColors;
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  isClient: boolean;
  isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const isClient = typeof window !== 'undefined';
  const [mode, setMode] = useState<ThemeMode>(getInitialThemeMode());
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  // No longer force light mode for any route; always respect user/system preference

  // Apply theme variables without modifying classes (for initial hydration)
  const applyThemeVariables = useCallback((themeToApply: ThemeColors, currentModeValue: string) => {
    if (typeof document === 'undefined') return;
    
    // Apply basic CSS variables
    Object.entries(cssVariableMap).forEach(([cssVar, themeKey]) => {
      document.documentElement.style.setProperty(
        cssVar, 
        themeToApply[themeKey as keyof ThemeColors] as string
      );
    });
    
    // Apply tag colors as CSS variables
    Object.entries(themeToApply.tagColors).forEach(([colorName, colorValue]) => {
      document.documentElement.style.setProperty(
        `--color-tag-${colorName}`,
        colorValue
      );
    });
    
    // Set the specific accent colors as direct CSS variables
    document.documentElement.style.setProperty('--color-blue', themeToApply.tagColors.blue);
    document.documentElement.style.setProperty('--color-red', themeToApply.tagColors.red);
    document.documentElement.style.setProperty('--color-green', themeToApply.tagColors.green);
    document.documentElement.style.setProperty('--color-yellow', themeToApply.tagColors.yellow);
    document.documentElement.style.setProperty('--color-orange', themeToApply.tagColors.orange);
    document.documentElement.style.setProperty('--color-pink', themeToApply.tagColors.pink);
    
    // Set additional UI-specific variables
    document.documentElement.style.setProperty('--color-background', themeToApply.background);
    document.documentElement.style.setProperty('--color-surface', themeToApply.cardBackground);
    document.documentElement.style.setProperty('--color-primary', themeToApply.primary);
    document.documentElement.style.setProperty('--color-primary-hover', themeToApply.primaryHover);
    document.documentElement.style.setProperty('--color-border', themeToApply.border);
    document.documentElement.style.setProperty('--color-text-primary', themeToApply.textPrimary);
    document.documentElement.style.setProperty('--color-text-secondary', themeToApply.textSecondary);
    document.documentElement.style.setProperty('--color-text-heading', themeToApply.textPrimary); // For heading text
    
    // Set additional dark mode variables for improved theme support
    document.documentElement.style.setProperty('--color-card-bg', themeToApply.cardBackground);
    document.documentElement.style.setProperty('--color-card-header-bg', themeToApply.cardBackgroundHeader);
    document.documentElement.style.setProperty('--color-input-bg', themeToApply.inputBackground);
    document.documentElement.style.setProperty('--color-hover-bg', themeToApply.hoverBackground);
    document.documentElement.style.setProperty('--color-selected-bg', themeToApply.selectedBackground);

    // Skeleton loading color - subtle gray that works in both themes
    const skeletonColor = currentModeValue === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';
    document.documentElement.style.setProperty('--color-skeleton', skeletonColor);
    
    // Apply a data attribute for potential CSS selectors
    document.documentElement.setAttribute('data-theme', currentModeValue);
    
    // Set the body background color and text color
    if (document.body) {
      document.body.style.backgroundColor = themeToApply.background;
      document.body.style.color = themeToApply.textPrimary;
    }
  }, []);
  
  // Apply theme to document
  const applyTheme = useCallback((themeToApply: ThemeColors, currentModeValue: ThemeMode) => {
    // Apply all the theme variables
    applyThemeVariables(themeToApply, currentModeValue);
    
    // Update class names for theme switching (not for initial hydration)
    // Apply to BOTH documentElement and body to ensure portals inherit theme
    if (currentModeValue === 'dark') {
      document.documentElement.classList.add('dark-mode');
      document.documentElement.classList.remove('light-mode');
      document.body.classList.add('dark-mode');
      document.body.classList.remove('light-mode');
    } else if (currentModeValue === 'light') {
      document.documentElement.classList.add('light-mode');
      document.documentElement.classList.remove('dark-mode');
      document.body.classList.add('light-mode');
      document.body.classList.remove('dark-mode');
    } else if (currentModeValue === 'system') {
      // For system, let the media query listener handle specific class if needed
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        document.documentElement.classList.add('dark-mode');
        document.documentElement.classList.remove('light-mode');
        document.body.classList.add('dark-mode');
        document.body.classList.remove('light-mode');
      } else {
        document.documentElement.classList.add('light-mode');
        document.documentElement.classList.remove('dark-mode');
        document.body.classList.add('light-mode');
        document.body.classList.remove('dark-mode');
      }
    }
  }, [applyThemeVariables]);
  
  // Watch for system preference changes - ONLY if mode is 'system'
  useEffect(() => {
    if (!isClient || mode !== 'system') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode, isClient]);

  const resolvedTheme = useMemo<ThemeColors>(() => {
    if (mode === 'dark') return darkTheme;
    if (mode === 'light') return lightTheme;
    return systemPrefersDark ? darkTheme : lightTheme;
  }, [mode, systemPrefersDark]);

  const resolvedThemeLabel: ThemeMode = useMemo(() => {
    if (mode === 'system') {
      return systemPrefersDark ? 'dark' : 'light';
    }
    return mode;
  }, [mode, systemPrefersDark]);

  // Apply theme side effects when the resolved theme changes.
  useEffect(() => {
    if (!isClient) return;
    applyTheme(resolvedTheme, resolvedThemeLabel);
    setStorageItem(STORAGE_KEYS.THEME_PREFERENCE, mode);
    reduxLogger.info('[ThemeProvider] Theme applied', { mode, resolvedTheme: resolvedThemeLabel });
  }, [mode, isClient, resolvedTheme, resolvedThemeLabel, applyTheme]);

  // Compute isDarkMode for components that need a simple boolean
  const isDarkMode = resolvedThemeLabel === 'dark';

  const contextValue = useMemo(() => ({
    theme: resolvedTheme, mode, setMode, isClient, isDarkMode
  }), [resolvedTheme, mode, setMode, isClient, isDarkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
} 
